import { useCallback, useEffect, useMemo, useState } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { LanguageProvider, useLang } from "@/i18n";
import { Header } from "@/components/Header";
import { FiltersBar } from "@/components/FiltersBar";
import { PlayerCard } from "@/components/PlayerCard";
import { HereWeGoModal } from "@/components/HereWeGoModal";
import { RegisterModal } from "@/components/RegisterModal";
import { Clubhouse } from "@/components/Clubhouse";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { AlertTriangle, FileSignature, Newspaper, Radar, Users } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ALL = "all";

// ---------- "Ask the Chief Scout" simulated AI search ----------
const STOPWORDS = new Set([
  "i", "need", "a", "an", "the", "who", "that", "shows", "show", "up", "for",
  "me", "my", "we", "with", "and", "is", "of", "to", "on", "in", "every",
  "some", "someone", "player", "guy", "team", "looking", "want", "wants",
  "find", "get", "give", "please", "really", "good", "at",
]);

const SYNONYM_RULES = [
  {
    words: ["fast", "quick", "speed", "speedy", "rapid", "pacey", "pace", "sprinter", "rapido", "rápido", "hızlı", "vite", "rapide"],
    test: (p) => (p.stats?.pace ?? 0) >= 65,
  },
  {
    words: ["defender", "defenders", "defense", "defence", "defensive", "tackler", "wall", "stopper", "cb", "back"],
    test: (p) => p.position === "Defender" || (p.stats?.defense ?? 0) >= 70,
  },
  {
    words: ["passer", "passing", "pass", "passes", "vision", "playmaker", "creative", "technical"],
    test: (p) => (p.stats?.passing ?? 0) >= 70 || /playmaker/i.test(p.style),
  },
  {
    words: ["striker", "forward", "forwards", "scorer", "goals", "goal", "attacker", "attack", "winger", "finisher"],
    test: (p) => p.position === "Forward",
  },
  {
    words: ["midfielder", "midfield", "engine", "mid", "box-to-box"],
    test: (p) => p.position === "Midfielder",
  },
  {
    words: ["keeper", "goalkeeper", "goalie", "gk", "shot-stopper"],
    test: (p) => p.position === "Goalkeeper",
  },
  {
    words: ["reliable", "committed", "dependable", "saturday", "saturdays", "sunday", "sundays", "weekend", "weekends", "always", "consistent"],
    test: (p) => (p.commitment || "").toLowerCase().includes("100% weekend"),
  },
  {
    words: ["video", "tape", "highlights", "highlight", "footage", "film"],
    test: (p) => !!p.videoHighlight,
  },
];

export function matchesSearch(player, query) {
  const q = (query || "").trim().toLowerCase();
  if (!q) return true;
  const tokens = q.split(/[^a-z0-9%\u00c0-\u017f]+/i).filter((w) => w && !STOPWORDS.has(w));
  if (tokens.length === 0) return true;
  const haystack = [player.name, player.city, player.position, player.style, player.commitment, player.leaguePreference, player.scoutingOrigin]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return tokens.every((tok) => {
    if (haystack.includes(tok)) return true;
    const rule = SYNONYM_RULES.find((r) => r.words.includes(tok));
    return rule ? rule.test(player) : false;
  });
}
// ----------------------------------------------------------------

const TabBar = ({ activeTab, onChange }) => {
  const { t } = useLang();
  const tabs = [
    { id: "market", label: t("transferMarket"), icon: Radar, testId: "transfer-market-tab" },
    { id: "clubhouse", label: t("clubhouse"), icon: Users, testId: "clubhouse-tab" },
  ];
  return (
    <div className="bg-[hsl(var(--navy-2))] border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex gap-1">
          {tabs.map(({ id, label, icon: Icon, testId }) => {
            const active = activeTab === id;
            return (
              <button
                key={id}
                data-testid={testId}
                onClick={() => onChange(id)}
                className={
                  active
                    ? "relative flex items-center gap-2 px-4 sm:px-6 py-3 text-sm font-semibold bg-[hsl(var(--background))] text-[hsl(var(--navy))] rounded-t-lg -mb-px transition-colors"
                    : "relative flex items-center gap-2 px-4 sm:px-6 py-3 text-sm font-semibold text-white/70 hover:text-white transition-colors"
                }
              >
                <Icon className="h-4 w-4" />
                {label}
                {active && (
                  <span className="absolute top-0 inset-x-0 h-[3px] bg-[hsl(var(--news-red))] rounded-t" />
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { t } = useLang();
  const [players, setPlayers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [postsLoading, setPostsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signingId, setSigningId] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [signedPlayer, setSignedPlayer] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("market");

  const [cityFilter, setCityFilter] = useState(ALL);
  const [positionFilter, setPositionFilter] = useState(ALL);
  const [styleFilter, setStyleFilter] = useState(ALL);
  const [commitmentFilter, setCommitmentFilter] = useState(ALL);
  const [leagueFilter, setLeagueFilter] = useState(ALL);
  const [searchQuery, setSearchQuery] = useState("");

  const fetchPlayers = useCallback(async () => {
    try {
      setError(null);
      const res = await axios.get(`${API}/players`);
      setPlayers(res.data);
    } catch (e) {
      console.error("Failed to load players", e);
      setError(t("connectionError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const fetchPosts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/posts`);
      setPosts(res.data);
    } catch (e) {
      console.error("Failed to load posts", e);
    } finally {
      setPostsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlayers();
    fetchPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSign = async (player) => {
    if (signingId) return;
    setSigningId(player.id);
    try {
      const res = await axios.post(`${API}/players/${player.id}/sign`);
      const updated = res.data;
      setPlayers((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      setSignedPlayer(updated);
      setModalOpen(true);
    } catch (e) {
      console.error("Failed to sign player", e);
      toast.error(t("toastSignError"));
    } finally {
      setSigningId(null);
    }
  };

  const handleReset = async () => {
    if (resetting) return;
    setResetting(true);
    try {
      await axios.post(`${API}/players/reset`);
      setPlayers((prev) => prev.map((p) => ({ ...p, status: "Free Agent" })));
      setModalOpen(false);
      toast.success(t("toastReset"));
    } catch (e) {
      console.error("Failed to reset market", e);
      toast.error(t("toastResetError"));
    } finally {
      setResetting(false);
    }
  };

  const handleRegister = async (payload) => {
    const res = await axios.post(`${API}/players`, payload);
    const newPlayer = res.data;
    setPlayers((prev) => [...prev, newPlayer]);
    toast.success(t("toastRegistered", { name: newPlayer.name }));
    return newPlayer;
  };

  const handleCreatePost = async (payload) => {
    const res = await axios.post(`${API}/posts`, payload);
    setPosts((prev) => [res.data, ...prev]);
    toast.success(t("toastPosted"));
    return res.data;
  };

  const handleToggleLike = async (postId, like) => {
    // Optimistic update
    setPosts((prev) =>
      prev.map((p) =>
        p.id === postId ? { ...p, likes: Math.max(0, p.likes + (like ? 1 : -1)) } : p
      )
    );
    try {
      const res = await axios.post(`${API}/posts/${postId}/like`, { like });
      setPosts((prev) => prev.map((p) => (p.id === postId ? res.data : p)));
    } catch (e) {
      console.error("Failed to toggle like", e);
      // Revert on failure
      setPosts((prev) =>
        prev.map((p) =>
          p.id === postId ? { ...p, likes: Math.max(0, p.likes + (like ? -1 : 1)) } : p
        )
      );
    }
  };

  const handleAddComment = async (postId, text) => {
    const res = await axios.post(`${API}/posts/${postId}/comments`, { text });
    setPosts((prev) => prev.map((p) => (p.id === postId ? res.data : p)));
    return res.data;
  };

  const resetFilters = () => {
    setPositionFilter(ALL);
    setStyleFilter(ALL);
    setCommitmentFilter(ALL);
    setLeagueFilter(ALL);
    setSearchQuery("");
  };

  // Dynamic filter options derived from the live talent pool
  const options = useMemo(
    () => ({
      positions: [...new Set(players.map((p) => p.position))],
      styles: [...new Set(players.map((p) => p.style))],
      commitments: [...new Set(players.map((p) => p.commitment))],
    }),
    [players]
  );

  const matchesFilters = useCallback(
    (p) =>
      (cityFilter === ALL || p.city === cityFilter) &&
      (positionFilter === ALL || p.position === positionFilter) &&
      (styleFilter === ALL || p.style === styleFilter) &&
      (commitmentFilter === ALL || p.commitment === commitmentFilter) &&
      (leagueFilter === ALL || p.leaguePreference === leagueFilter) &&
      matchesSearch(p, searchQuery),
    [cityFilter, positionFilter, styleFilter, commitmentFilter, leagueFilter, searchQuery]
  );

  const freeAgents = useMemo(
    () => players.filter((p) => p.status === "Free Agent" && matchesFilters(p)),
    [players, matchesFilters]
  );
  const signedPlayers = useMemo(
    () => players.filter((p) => p.status === "Signed" && matchesFilters(p)),
    [players, matchesFilters]
  );
  // Counter respects the selected market
  const totalFreeAgents = useMemo(
    () =>
      players.filter(
        (p) => p.status === "Free Agent" && (cityFilter === ALL || p.city === cityFilter)
      ).length,
    [players, cityFilter]
  );
  // Clubhouse posts respect the selected market
  const cityPosts = useMemo(
    () => posts.filter((p) => cityFilter === ALL || p.city === cityFilter),
    [posts, cityFilter]
  );

  const filtersActive =
    positionFilter !== ALL ||
    styleFilter !== ALL ||
    commitmentFilter !== ALL ||
    leagueFilter !== ALL ||
    searchQuery.trim() !== "";

  return (
    <div className="App noise-bg bg-[hsl(var(--background))] min-h-screen">
      <Header
        totalFreeAgents={totalFreeAgents}
        onReset={handleReset}
        resetting={resetting}
        loading={loading}
        city={cityFilter}
        onCityChange={setCityFilter}
        onOpenRegister={() => setRegisterOpen(true)}
      />

      <TabBar activeTab={activeTab} onChange={setActiveTab} />

      {activeTab === "market" && (
        <FiltersBar
          position={positionFilter}
          style={styleFilter}
          commitment={commitmentFilter}
          league={leagueFilter}
          search={searchQuery}
          onPositionChange={setPositionFilter}
          onStyleChange={setStyleFilter}
          onCommitmentChange={setCommitmentFilter}
          onLeagueChange={setLeagueFilter}
          onSearchChange={setSearchQuery}
          onClear={resetFilters}
          filtersActive={filtersActive}
          options={options}
        />
      )}

      {activeTab === "clubhouse" ? (
        <Clubhouse
          posts={cityPosts}
          loading={postsLoading}
          city={cityFilter}
          onCreatePost={handleCreatePost}
          onToggleLike={handleToggleLike}
          onAddComment={handleAddComment}
        />
      ) : (
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          {error && (
            <Card
              data-testid="free-agents-error"
              className="border-[hsl(var(--news-red))] bg-[hsl(var(--surface))] mb-8"
            >
              <CardContent className="flex flex-col sm:flex-row items-center gap-4 py-6">
                <AlertTriangle className="h-8 w-8 text-[hsl(var(--news-red))] shrink-0" />
                <div className="flex-1 text-center sm:text-start">
                  <p className="font-semibold text-[hsl(var(--ink))]">{t("signalLost")}</p>
                  <p className="text-sm text-[hsl(var(--muted-foreground))]">{error}</p>
                </div>
                <Button
                  data-testid="retry-button"
                  onClick={() => {
                    setLoading(true);
                    fetchPlayers();
                  }}
                  className="bg-[hsl(var(--news-red))] text-white hover:bg-[hsl(var(--news-red-dark))]"
                >
                  {t("retry")}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* FREE AGENTS SECTION */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-1">
              <span className="h-[3px] w-8 bg-[hsl(var(--news-red))] rounded-full" />
              <span className="font-display uppercase tracking-[0.18em] text-xs text-[hsl(var(--news-red))]">
                {t("breakingAvailable")}
              </span>
            </div>
            <div className="flex items-center gap-2 mb-5">
              <Radar className="h-5 w-5 text-[hsl(var(--navy))]" />
              <h2 className="text-lg sm:text-xl font-semibold text-[hsl(var(--ink))]">
                {t("freeAgents")}
                {cityFilter !== ALL && (
                  <span className="text-[hsl(var(--news-red))]"> · {cityFilter}</span>
                )}
              </h2>
              <span className="text-sm text-[hsl(var(--muted-foreground))] tabular-nums">
                ({loading ? "…" : freeAgents.length})
              </span>
            </div>

            {loading ? (
              <div
                data-testid="free-agents-loading"
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6"
              >
                {[...Array(6)].map((_, i) => (
                  <Card key={i} className="bg-[hsl(var(--card))]">
                    <CardContent className="p-5 space-y-4">
                      <Skeleton className="h-6 w-3/4" />
                      <div className="flex gap-2">
                        <Skeleton className="h-5 w-20" />
                        <Skeleton className="h-5 w-24" />
                      </div>
                      <Skeleton className="h-2 w-full" />
                      <Skeleton className="h-2 w-full" />
                      <Skeleton className="h-10 w-full" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : freeAgents.length === 0 && !error ? (
              <Card data-testid="free-agents-empty" className="bg-[hsl(var(--surface))]">
                <CardContent className="py-10 text-center space-y-3">
                  <p className="font-display uppercase tracking-wide text-2xl text-[hsl(var(--muted-foreground))]">
                    {t("marketSwept")}
                  </p>
                  <p className="text-sm text-[hsl(var(--muted-foreground))]">
                    {filtersActive || cityFilter !== ALL ? t("noMatch") : t("allSigned")}
                  </p>
                  {filtersActive && (
                    <Button
                      data-testid="clear-filters-button"
                      variant="outline"
                      onClick={resetFilters}
                      className="border-[hsl(var(--border))] text-[hsl(var(--navy))] hover:bg-[hsl(var(--accent))]"
                    >
                      {t("resetFiltersBtn")}
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                <AnimatePresence mode="popLayout">
                  {freeAgents.map((player) => (
                    <motion.div
                      key={player.id}
                      layout
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.96 }}
                      transition={{ duration: 0.18, ease: "easeOut" }}
                    >
                      <PlayerCard
                        player={player}
                        onSign={handleSign}
                        signing={signingId === player.id}
                      />
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            )}
          </section>

          {/* SIGNED SECTION */}
          {!loading && signedPlayers.length > 0 && (
            <section data-testid="completed-transfers-section">
              <Separator className="mb-6" />
              <div className="flex items-center gap-3 mb-1">
                <span className="h-[3px] w-8 bg-[hsl(var(--muted-foreground))] rounded-full" />
                <span className="font-display uppercase tracking-[0.18em] text-xs text-[hsl(var(--muted-foreground))]">
                  {t("dealsDone")}
                </span>
              </div>
              <div className="flex items-center gap-2 mb-5">
                <FileSignature className="h-5 w-5 text-[hsl(var(--muted-foreground))]" />
                <h2 className="text-lg sm:text-xl font-semibold text-[hsl(var(--ink))]">
                  {t("signedOffMarket")}
                </h2>
                <span className="text-sm text-[hsl(var(--muted-foreground))] tabular-nums">
                  ({signedPlayers.length})
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <AnimatePresence mode="popLayout">
                  {signedPlayers.map((player) => (
                    <motion.div
                      key={player.id}
                      layout
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.96 }}
                      transition={{ duration: 0.18, ease: "easeOut" }}
                    >
                      <PlayerCard player={player} signed />
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </section>
          )}

          <footer className="mt-14 pb-8 text-center">
            <p className="text-xs text-[hsl(var(--muted-foreground))]">{t("footer")}</p>
          </footer>
        </main>
      )}

      <HereWeGoModal
        open={modalOpen}
        player={signedPlayer}
        onClose={() => setModalOpen(false)}
      />

      <RegisterModal
        open={registerOpen}
        onClose={() => setRegisterOpen(false)}
        onRegister={handleRegister}
      />

      <Toaster position="bottom-right" richColors />
    </div>
  );
};

function App() {
  return (
    <LanguageProvider>
      <Dashboard />
    </LanguageProvider>
  );
}

export default App;
